/**
 * WorkflowStatusBadge - Displays current workflow state with visual cues
 * Requirements: 18.9
 */

import React from 'react';
import type { PublishLifecycleState, WorkflowState } from '@course-authoring/shared-types';
import { designTokens } from '@course-authoring/shared-types';

/**
 * Display states: the three workflowState values plus 'editing' — a
 * display-only nuance for published content with pending edits ("Published —
 * editing"). It never feeds back into workflowState (hoot jobs key off that
 * enum); derive it with `deriveBadgeState`.
 */
export type WorkflowBadgeState = WorkflowState | 'editing';

export interface WorkflowStatusBadgeProps {
  state: WorkflowBadgeState;
  className?: string;
}

/**
 * Collapses a row's workflowState + context lifecycle into the badge's display
 * state: published content mid-edit shows as 'editing'; everything else keeps
 * its workflowState pill.
 */
export const deriveBadgeState = (
  workflowState: WorkflowState,
  publishLifecycle?: PublishLifecycleState,
): WorkflowBadgeState =>
  workflowState === 'published' && publishLifecycle === 'editing' ? 'editing' : workflowState;

// Copy-system labels (publish-export-workflow spec).
const STATE_LABELS: Record<WorkflowBadgeState, string> = {
  draft: 'Draft',
  published: 'Published',
  editing: 'Published — editing',
  archived: 'Archived',
};

/**
 * Get the color for a workflow state
 */
const getStateColor = (state: WorkflowBadgeState): string => {
  const colors: Record<WorkflowBadgeState, string> = {
    draft: designTokens.colors.workflowDraft,
    published: designTokens.colors.workflowPublished,
    editing: designTokens.colors.workflowDraft,
    archived: designTokens.colors.workflowArchived,
  };
  return colors[state];
};

/**
 * Get the background color for a workflow state (lighter version).
 * Tailwind variants with no designTokens equivalents yet — they await an
 * editor-chrome palette decision (CONV-6); the foreground colors above DO
 * come from designTokens.colors.workflow*.
 */
const getStateBackgroundColor = (state: WorkflowBadgeState): string => {
  const backgrounds: Record<WorkflowBadgeState, string> = {
    draft: '#fef3c7', // Amber-100
    published: '#cffafe', // Cyan-100
    editing: '#fffbeb', // Amber-50 — matches the "unpublished edits" banner family
    archived: '#f3f4f6', // Gray-100
  };
  return backgrounds[state];
};

export const WorkflowStatusBadge: React.FC<WorkflowStatusBadgeProps> = ({
  state,
  className = '',
}) => {
  const displayName = STATE_LABELS[state];
  const color = getStateColor(state);
  const backgroundColor = getStateBackgroundColor(state);

  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 12px',
        borderRadius: '9999px',
        fontSize: '14px',
        fontWeight: 500,
        color: color,
        backgroundColor: backgroundColor,
        border: `1px solid ${color}`,
      }}
      role="status"
      aria-label={`Workflow status: ${displayName}`}
    >
      <span
        style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: color,
        }}
        aria-hidden="true"
      />
      {displayName}
    </span>
  );
};
